import React, {useEffect, useState} from "react";
import {makeStyles} from "@mui/styles";
import Paper from "@mui/material/Paper";
import {
    Container,
    Grid,
    List,
    ListItem,
    Box,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useSelector, useDispatch} from "react-redux";
import RoomBox from "./RoomBox";
import NautilusTextInput from "../../Common/NautilusTextInput";
import {useAuthUser, useIsAuthenticated} from "react-auth-kit";
import {
    isDealerSite,
    getRefundable,
} from "../../../utils/utils";
import {setTransactionRefundable} from "../../../redux/actions/transactionActions";
import {setTotals} from "../../../redux/actions/totalActions";
import {differenceInDays, parse} from "date-fns";

const useStyles = makeStyles({
    root: {
        width: "100%",
    },
    container: {
        marginTop: 20,
        paddingBottom: '30px'
    },
    tableContainer: {
        marginTop: 50,
    },
    info: {
        display: 'grid',
        gridTemplateColumns: "1fr minmax(100px, auto)",
        gap: '20px',
        "@media (max-width: 767px)": {
            display: 'flex',
            flexDirection: 'column !important',
        }
    },
    table: {
        border: '1px solid rgba(0, 0, 0, 0.87) !important',
        borderBottom: '0',
    },
    tableRow: {
        display: "grid",
        gridTemplateColumns: "minmax(350px, auto) minmax(150px, auto) minmax(150px, auto) minmax(250px, auto)",
        borderBottom: '1px solid rgba(0, 0, 0, 0.87)',
        "@media (max-width: 1000px)": {
            gridTemplateColumns: "minmax(250px, auto) 150px 150px 100px",
        },
        "@media (max-width: 700px)": {
            gridTemplateColumns: "1fr 100px",
        },
    },
    tableRowSecond: {
        display: "grid",
        gridTemplateColumns: "1fr 150px",
        "@media (max-width: 1000px)": {
            gridTemplateColumns: "1fr 100px",
        },
    },
    borderRight: {
        borderRight: '1px solid rgba(0, 0, 0, 0.87)',
        "@media (max-width: 600px)": {
            textAlign: 'left !important'
        }
    },
});

const Review = ({
                    clientBookingRef,
                    setClientBookingRef,
                    clientName,
                    setClientName,
                }) => {
    const {dealer} = window;
    const {data: trip} = useSelector((state) => state.trip);
    const rooms = useSelector((state) => state.rooms);
    const tax = useSelector((state) => state.tax);
    const totals = useSelector((state) => state.totals);
    const parkFee = useSelector((state) => state.parkFee);
    const transactions = useSelector((state) => state.transactions);
    const selectedRooms = transactions?.selectedRooms || [];
    const auth = useAuthUser();
    const isAuth = useIsAuthenticated();
    const apiURL = document.head.querySelector('meta[name="api-url"]');
    const feeComment = trip?.portFee?.feeComment;
    const dispatch = useDispatch();
    const boardDate = parse(trip.boarddate, 'yyyy-MM-dd', new Date());
    const daysBeforeBoardDate = differenceInDays(boardDate, new Date());
    const disabledRefundableByDates = daysBeforeBoardDate < 60;
    // Check if at least one room has refundableDisabled set to true
    const disabledRefundable = rooms.some(room => room.price.refundableDisabled);

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [longDescriptionParts, setLongDescriptionParts] = useState([]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);


    const getSpacesByRoom = (roomIndex) =>
        selectedRooms.filter((r) => r.index === roomIndex);
    const hasSelectedSpaces = (roomIndex) =>
        getSpacesByRoom(roomIndex).length > 0;
    // console.log(auth());
    const classes = useStyles();
    const [feeCheckbox, setFeeCheckbox] = useState(false);

    useEffect(() => {
        if (rooms && rooms.length > 0) {
            rooms.map((room) => {
                if (hasSelectedSpaces(room.index)) {
                    const roomRefundable = getRefundable(
                        room,
                        getSpacesByRoom(room.index)
                    );
                    const newPrice = feeCheckbox ? roomRefundable : 0;
                    dispatch(setTransactionRefundable(newPrice));
                    dispatch(setTotals());
                }
            });
        }

    }, [rooms, feeCheckbox]);

    useEffect(() => {
        if (Object.keys(parkFee).length > 0 && parkFee?.longDescription?.includes('[CLICK HERE]')) {
            const data = parkFee.longDescription.split('[CLICK HERE]');

            setLongDescriptionParts(data);
        }
    }, [parkFee]);


    return (
        <Container maxWidth="lg" className={classes.container}>
            <Box
                sx={{
                    marginBottom: "20px",
                    padding: "20px",
                    backgroundColor: '#EEEEEE',
                    boxShadow: '0px 1px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)'
                }}
            >
                <Typography variant="h4" component="h4" sx={{fontSize: '1.6rem'}} align="left">
                    Please note that certain activities may require physical mobility. For guests with reduced mobility,
                    please contact us in advance so we can provide the best possible experience. </Typography>
                <Typography variant="h4" component="h4" sx={{marginTop: '10px', fontSize: '1.6rem'}} align="left">
                    Choice of cabin selection is first-come, first-served upon our receipt
                    of your initial payment. Special requests, dietary, rentals, special
                    deals and pre and post trip adventures accommodated with pleasure
                    later in the booking process.
                </Typography>
            </Box>
            <Paper className={classes.root}>
                <List style={{width: "100%", padding: 0}}>
                    <ListItem
                        alignItems={"flex-start"}
                        style={{
                            padding: 10,
                            margin: 0,
                            backgroundColor: "#000000",
                            color: "#ffffff",
                        }}
                        className="pricingMobile"
                    >
                        <Grid
                            container
                            direction="row"
                            spacing={3}
                            alignItems={"center"}
                            justify={"center"}
                        >
                            <Grid item sm={4}/>
                            <Grid item sm={3}>
                                <Typography
                                    variant="h5"
                                    component="h5"
                                    align="center"
                                    style={{fontSize: "1.8rem"}}
                                >
                                    Room Type
                                </Typography>
                            </Grid>
                            <Grid item sm={2}>
                                <Typography
                                    variant="h5"
                                    component="h5"
                                    align="center"
                                    style={{fontSize: "1.8rem"}}
                                >
                                    No. of Spaces
                                </Typography>
                            </Grid>
                            <Grid item sm={3}>
                                <Typography
                                    variant="h5"
                                    component="h5"
                                    align="center"
                                    style={{fontSize: "1.8rem"}}
                                >
                                    Price
                                </Typography>
                            </Grid>
                        </Grid>
                    </ListItem>

                    {rooms &&
                        rooms.length > 0 &&
                        rooms.map(
                            (room) =>
                                hasSelectedSpaces(room.index) && (
                                    <RoomBox
                                        key={room.index}
                                        room={room}
                                        selectedSpaces={getSpacesByRoom(room.index)}
                                        feeCheckbox={feeCheckbox}
                                    />
                                )
                        )}

                    <ListItem
                        alignItems={"flex-start"}
                        style={{paddingTop: 12, paddingBottom: 12, margin: 0}}
                        className="pricingMobile"
                    >
                        <Grid
                            container
                            direction="row"
                            spacing={3}
                            alignItems={"center"}
                            justify={"center"}
                        >
                            <Grid item sm={9}>
                                <Typography variant="h4" component="h4" align="left">
                                    Tax
                                    {/* ({tax * 100}%) */}
                                </Typography>
                            </Grid>
                            <Grid item sm={3}>
                                <Typography variant="h4" component="h4" align="center">
                                    ${totals.taxes.formatMoney(2)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </ListItem>
                    {isDealerSite() && isAuth() && (
                        <ListItem
                            alignItems={"flex-start"}
                            style={{paddingTop: 12, paddingBottom: 12, margin: 0}}
                            className="pricingMobile"
                        >
                            <Grid
                                container
                                direction="row"
                                spacing={3}
                                alignItems={"center"}
                                justify={"center"}
                            >
                                <Grid item sm={9}>
                                    <Typography variant="h4" component="h4" align="left">
                                        Commission ({auth().commission * 100}%)
                                    </Typography>
                                </Grid>
                                <Grid item sm={3}>
                                    <Typography variant="h4" component="h4" align="center">
                                        $
                                        {(
                                            (totals.total - totals.parkFee) *
                                            auth().commission
                                        ).formatMoney(2)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </ListItem>
                    )}

                    {totals.parkFee > 0 && (
                        <ListItem
                            alignItems={"flex-start"}
                            style={{paddingTop: 12, paddingBottom: 12, margin: 0}}
                            className="pricingMobile"
                        >
                            <Grid
                                container
                                direction="row"
                                spacing={3}
                                alignItems={"center"}
                                justify={"center"}
                            >
                                <Grid item sm={9}>
                                    <Typography variant="h4" component="h4" align="left">
                                        {trip.parkFee.description}
                                    </Typography>
                                    {trip.parkFee.redeemable > 0 && (
                                        <Typography variant="h5" component="h5" align="left">
                                            USD{totals.parkFeeCost.formatMoney(2)} Voucher per guest
                                            redeemable onboard, at SeeCreatures, or Baja Expeditions!
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item sm={3}>
                                    <Typography variant="h4" component="h4" align="center">
                                        ${totals.parkFee.formatMoney(2)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </ListItem>
                    )}

                    {totals.total > 0 && (
                        <ListItem
                            alignItems={"flex-start"}
                            style={{
                                paddingTop: 12,
                                paddingBottom: 12,
                                margin: 0,
                                backgroundColor: "#f2f2f2",
                            }}
                        >
                            <Grid
                                container
                                direction="row"
                                spacing={3}
                                alignItems={"center"}
                                justify={"center"}
                            >
                                <Grid item sm={9}>
                                    <Typography variant="h4" component="h4" align="left">
                                        {feeCheckbox && totals.totalRefundable > 0 ? (
                                            <strong>{`Total (refundable)`}</strong>
                                        ) : (
                                            <>
                                                <strong>{`Total`}</strong>&nbsp;
                                                {/* <span
                          style={{ fontSize: "14px" }}
                        >{!disabledRefundableByDates && !disabledRefundable && totals.totalRefundable >0 && `non refundable charter fee`}</span> */}
                                            </>
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item sm={3}>
                                    <Typography variant="h4" component="h4" align="center">
                                        <strong>
                                            $
                                            {(isDealerSite() && isAuth()
                                                    ? totals.total -
                                                    (totals.total - totals.parkFee) * auth().commission
                                                    : totals.total
                                            ).formatMoney(2)}
                                        </strong>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </ListItem>
                    )}
                </List>
            </Paper>
            <Typography variant="h5" component="h5" align="left">
                {feeComment && <i>* {feeComment}</i>}
            </Typography>
            {dealer && parseInt(dealer) === 1 && (
                <Paper className={classes.root} sx={{marginTop: "50px"}}>
                    <Grid container spacing={3}>
                        <Grid
                            item
                            xs={12}
                            style={{marginLeft: "20px", marginRight: "20px"}}
                        >
                            <Typography
                                variant="h3"
                                style={{fontWeight: "600", fontSize: "1.8rem"}}
                            >
                                Is This a Client Booking?
                            </Typography>
                            <Typography style={{marginTop: "10px"}}>
                                If this is for a booking within your own company and you have a
                                client name or booking reference number, please add these below.
                                These will be included in booking confirmation emails for
                                reference.
                            </Typography>
                        </Grid>
                        <Grid style={{margin: "20px", marginTop: "0"}} item xs={12}>
                            <NautilusTextInput
                                fullWidth
                                name="clientName"
                                value={clientName || ''}
                                onChange={(e) => setClientName(e.target.value)}
                                label="Booking Client Name"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid style={{margin: "20px", marginTop: "-20px"}} item xs={12}>
                            <NautilusTextInput
                                fullWidth
                                name="clientBookingRef"
                                value={clientBookingRef || ''}
                                onChange={(e) => setClientBookingRef(e.target.value)}
                                label="Booking Client Reference"
                                variant="outlined"
                            />
                        </Grid>
                    </Grid>
                </Paper>
            )}
            {!disabledRefundableByDates && !disabledRefundable && totals.totalRefundable > 0 &&
                <Paper sx={{marginTop: 5, marginBottom: 5, borderRadius: 0}}>
                    <Box sx={{padding: "30px 20px 40px 40px"}}>
                        <Box xs={12} sm={12}>
                            <FormControlLabel
                                // className={'cleanCheckbox'}
                                control={
                                    <Checkbox
                                        checked={feeCheckbox === true}
                                        name="feeCheckbox"
                                        style={{paddingLeft: 0}}
                                        onChange={() => setFeeCheckbox(!feeCheckbox)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="h4" component="h4" align="left">
                                        <strong>I would prefer a “no questions asked” refundable charter fee</strong>
                                    </Typography>
                                }
                            />
                        </Box>
                        <Typography
                            variant="h4"
                            component="h4"
                            style={{marginTop: "20px"}}
                            align="left"
                        >
                            It’s your option. Our best value non-refundable charter fee or for a small premium, at the
                            time of booking purchase our no-questions asked fully refundable charter fee
                        </Typography>
                    </Box>
                    <Grid
                        container
                        sx={{
                            padding: "15px 20px 15px 30px",
                            backgroundColor: "#EEEEEE",
                            borderTop: "1px solid rgba(0,0,0,0.2)",
                        }}
                    >
                        <Grid item xs={8} sm={9}>
                            <Typography variant="h4" component="h4" align="left">
                                <strong>{`Total (refundable)`}</strong>
                            </Typography>
                        </Grid>
                        <Grid item xs={4} sm={3}>
                            <Typography variant="h4" component="h4" align="right">
                                {/* <strong>${transactions.refundable && transactions.refundable.formatMoney(2)}</strong> */}
                                <strong>${totals.totalRefundable.formatMoney(2)}</strong>
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>}
            {trip?.location?.tripTypeFk !== 2 && Object.keys(parkFee).length > 0  && parkFee.description && parkFee.divingDays && parkFee.divingDays > 0 &&
                <Paper sx={{marginTop: 5, marginBottom: 5, borderRadius: 0}}>
                    <Box sx={{padding: "20px"}}>
                        {parkFee?.longDescription?.includes('[CLICK HERE]') && parkFee.longDescription.includes('**') && longDescriptionParts.length > 0 ?
                            <Grid item xs={12} className={classes.info}>
                                <Typography variant='p'>
                                    {longDescriptionParts[0].split(/\*\*(.*?)\*\*/).map((part, index) =>
                                        index % 2 === 1 ? <strong key={index}>{part}</strong> : part
                                    )}
                                    <a
                                        style={{color: '#448aff', textDecoration: 'none'}}
                                        href="https://nautilusliveaboards.com/wp-content/uploads/2025/01/2025-01-20-Mexico-Park-Fees.pdf"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        [Click Here]
                                    </a>
                                    {longDescriptionParts[1].split(/\*\*(.*?)\*\*/).map((part, index) =>
                                        index % 2 === 1 ? <strong key={index}>{part}</strong> : part
                                    )}
                                </Typography>
                                <div>
                                    <img src={`${window['appBasename']}/images/conanp.png`} alt='conanp'/>
                                </div>
                            </Grid>
                            : parkFee?.longDescription && <Grid item xs={12} className={classes.info}>
                            <Typography variant='p'>
                                {parkFee.longDescription}
                                <a
                                    style={{color: '#448aff', textDecoration: 'none'}}
                                    href="https://nautilusliveaboards.com/wp-content/uploads/2025/01/2025-01-20-Mexico-Park-Fees.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    [Click Here]
                                </a>
                            </Typography>
                            <div>
                                <img src={`${window['appBasename']}/images/conanp.png`} alt='conanp'/>
                            </div>
                        </Grid>
                        }
                        {parkFee.description && parkFee.divingDays && parkFee.divingDays > 0 && <Grid item xs={12}>
                            <div className={classes.tableContainer}>
                                <div className={classes.table}>
                                    <div className={`${classes.tableColumn} ${classes.tableRow}`}
                                         style={{backgroundColor: '#000'}}>
                                        <div className={classes.borderRight}
                                             style={{padding: '5px 7px', minHeight: '35px', color: '#fff'}}></div>
                                        {windowWidth > 700 && <>
                                            <div className={classes.borderRight} style={{
                                                textAlign: 'center',
                                                padding: '5px 7px',
                                                minHeight: '35px',
                                                color: '#fff'
                                            }}>Guests
                                            </div>
                                            <div className={classes.borderRight} style={{
                                                textAlign: 'center',
                                                padding: '5px 7px',
                                                minHeight: '35px',
                                                color: '#fff'
                                            }}>Days in the park
                                            </div>
                                        </>}
                                        <div style={{padding: '5px 7px', minHeight: '35px', color: '#fff'}}>Total</div>
                                    </div>
                                    <div className={classes.tableRow}>
                                        <div className={classes.borderRight}
                                             style={{padding: '5px 7px', minHeight: '35px'}}>
                                            {parkFee.description + ' $' + parkFee.perDay}/person/day
                                        </div>
                                        {window.innerWidth > 700 &&
                                            <>
                                                <div className={classes.borderRight} style={{
                                                    textAlign: 'center',
                                                    padding: '5px 7px',
                                                    minHeight: '35px'
                                                }}>
                                                    {rooms && rooms.length > 0 &&
                                                        rooms.map(
                                                            (room) =>
                                                                hasSelectedSpaces(room.index) && (
                                                                    getSpacesByRoom(room.index).length
                                                                )
                                                        )
                                                    }
                                                </div>
                                                <div className={classes.borderRight} style={{
                                                    textAlign: 'center',
                                                    padding: '5px 7px',
                                                    minHeight: '35px'
                                                }}>
                                                    {parkFee.divingDays}
                                                </div>
                                            </>
                                        }
                                        <div style={{padding: '5px 7px', minHeight: '35px'}}>
                                            ${
                                            parkFee.total *
                                            (rooms && rooms.length > 0
                                                ? rooms.reduce((acc, room) => {
                                                    return hasSelectedSpaces(room.index)
                                                        ? acc + getSpacesByRoom(room.index).length
                                                        : acc;
                                                }, 0)
                                                : 1)
                                        }
                                        </div>
                                    </div>
                                    {windowWidth <= 700 && <>
                                        <div className={classes.tableRowSecond} style={{
                                            borderBottom: '1px solid rgba(0, 0, 0, 0.87)',
                                            backgroundColor: '#F2F2F2'
                                        }}>
                                            <div className={classes.borderRight}
                                                 style={{padding: '5px 7px', minHeight: '35px'}}>
                                                Guests
                                            </div>
                                            <div style={{padding: '5px 7px', minHeight: '35px'}}>
                                                {rooms && rooms.length > 0 &&
                                                    rooms.map(
                                                        (room) =>
                                                            hasSelectedSpaces(room.index) && (
                                                                getSpacesByRoom(room.index).length
                                                            )
                                                    )
                                                }
                                            </div>
                                        </div>
                                        <div className={classes.tableRowSecond}
                                             style={{borderBottom: '1px solid rgba(0, 0, 0, 0.87)'}}>
                                            <div className={classes.borderRight}
                                                 style={{padding: '5px 7px', minHeight: '35px'}}>
                                                Days in the park
                                            </div>
                                            <div style={{padding: '5px 7px', minHeight: '35px'}}>
                                                {parkFee.divingDays}
                                            </div>
                                        </div>
                                    </>}
                                </div>
                            </div>
                        </Grid>
                        }
                    </Box>
                </Paper>
            }
        </Container>
    );
};

export default Review;